import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../hooks/useAuth';
import { submissionsService } from '../../services/submissions.service';
import AuthModal from '../../components/auth/AuthModal';

const { FiFileText, FiLink, FiEdit3, FiCheckCircle } = FiIcons;

export default function SubmissionCenter() {
  const { contentLanguage } = useTranslation();
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    type: 'resource',
    title: '',
    url: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submissionsService.submitContent(formData.type, {
        title: formData.title,
        url: formData.url,
        description: formData.description
      });
      setSuccess(true);
      setFormData({ type: 'resource', title: '', url: '', description: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="inline-flex p-4 bg-green-50 text-green-600 rounded-full mb-6">
          <SafeIcon icon={FiCheckCircle} className="text-5xl" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 mb-4">
          {contentLanguage === 'el' ? 'Ευχαριστούμε!' : 'Thank You!'}
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          {contentLanguage === 'el' 
            ? 'Η υποβολή σας παραλήφθηκε και θα ελεγχθεί σύντομα.' 
            : 'Your submission has been received and will be reviewed shortly.'}
        </p>
        <button 
          onClick={() => setSuccess(false)}
          className="px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition"
        >
          {contentLanguage === 'el' ? 'Υποβολή άλλου' : 'Submit Another'}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-black text-gray-900 mb-4">
          {contentLanguage === 'el' ? 'Κέντρο Υποβολών' : 'Submission Center'}
        </h1>
        <p className="text-xl text-gray-600">
          {contentLanguage === 'el' 
            ? 'Μοιραστείτε γνώσεις για να βοηθήσετε άλλους ασθενείς.' 
            : 'Share knowledge to help other patients.'}
        </p>
      </div>

      <div className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              {contentLanguage === 'el' ? 'Τύπος Υποβολής' : 'Submission Type'}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { id: 'resource', label: 'Link / Article', icon: FiLink },
                { id: 'tip', label: 'Practical Tip', icon: FiEdit3 },
                { id: 'PDF', label: 'Document', icon: FiFileText }
              ].map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, type: type.id })}
                  className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                    formData.type === type.id 
                      ? 'border-blue-600 bg-blue-50 text-blue-700 font-bold' 
                      : 'border-gray-100 bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={type.icon} />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              {contentLanguage === 'el' ? 'Τίτλος' : 'Title'}
            </label>
            <input 
              type="text" required
              value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0"
              placeholder={contentLanguage === 'el' ? 'Σύντομος τίτλος...' : 'Short title...'}
            />
          </div>

          {formData.type !== 'tip' && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                URL
              </label>
              <input 
                type="url" required={(formData.type === 'resource')}
                value={formData.url} onChange={e => setFormData({ ...formData, url: e.target.value })}
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0"
                placeholder="https://"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              {contentLanguage === 'el' ? 'Περιγραφή ή Σχόλια' : 'Description or Comments'}
            </label>
            <textarea 
              rows="4" required
              value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0"
              placeholder={contentLanguage === 'el' ? 'Γιατί είναι χρήσιμο;' : 'Why is this helpful?'}
            />
          </div>

          {error && (
            <div className="p-4 bg-red-50 text-red-600 rounded-xl text-sm font-medium">
              {error}
            </div>
          )}

          <button 
            type="submit" disabled={submitting}
            className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {submitting 
              ? '...' 
              : (!user 
                  ? (contentLanguage === 'el' ? 'Σύνδεση για Υποβολή' : 'Sign in to Submit') 
                  : (contentLanguage === 'el' ? 'Υποβολή' : 'Submit'))}
          </button>
        </form>
      </div>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}